import { insightFor, type WordInsight } from '@lexicon/core';
import { Sheet } from './Sheet';
import { ChevronRightIcon } from './icons';
import { useStrings } from '../state/AppContext';

const NO_SPENDING_WORDS: ReadonlySet<string> = new Set();

export interface InsightSheetProps {
  open: boolean;
  onClose: () => void;
  /** Unique words guessed this game, in order. */
  words: readonly string[];
  insightsLeft: number;
  /**
   * Words already paid for in this session. `null` means "revealed, but
   * this word's dictionary chunk failed to load" (spec §3.7) — the word
   * must still read as revealed (no Reveal button, no re-charge), just
   * with its definition unavailable rather than dropped.
   */
  revealed: Record<string, WordInsight | null>;
  onReveal: (word: string) => void;
  /** Injectable for tests. */
  lookup?: (word: string) => WordInsight | null;
  /**
   * Words whose spend is currently in flight. Their Reveal button disables
   * individually so a same-tick double-click can't charge twice for one
   * reveal (mirrors the Hint footer button's in-flight guard).
   */
  spendingWords?: ReadonlySet<string>;
}

export function InsightSheet({
  open, onClose, words, insightsLeft, revealed, onReveal, lookup = insightFor,
  spendingWords = NO_SPENDING_WORDS,
}: InsightSheetProps) {
  const t = useStrings();
  return (
    <Sheet open={open} title={t.insight.title} onClose={onClose}>
      {words.length === 0 ? (
        <p className="pb-4 text-sm text-dim">{t.insight.guessFirst}</p>
      ) : (
        <ul className="flex flex-col gap-2 pb-4">
          {words.map((word) => {
            // Presence of the key (not truthiness of its value) is what
            // "already revealed" means — a `null` value is a revealed word
            // whose definition could not be loaded, not an unrevealed one
            // (F2 review fix: dropping it here is what let it be re-charged).
            const isRevealed = Object.prototype.hasOwnProperty.call(revealed, word);
            const shown = revealed[word];
            const label = word.toUpperCase();

            if (isRevealed) {
              return (
                <li key={word} className="rounded-xl bg-black/20 p-3">
                  <p className="text-sm font-semibold tracking-[0.2em]">{label}</p>
                  {shown ? (
                    <>
                      <p className="mt-1 text-sm text-ink">{shown.def}</p>
                      {shown.origin ? (
                        <p className="mt-1 text-xs text-dim">{t.insight.origin(shown.origin)}</p>
                      ) : null}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-dim">{t.insight.unavailable}</p>
                  )}
                </li>
              );
            }

            const entry = lookup(word);
            if (!entry) {
              return (
                <li
                  key={word}
                  className="flex items-center justify-between rounded-xl bg-black/10 p-3 opacity-60"
                >
                  <span className="text-sm font-semibold tracking-[0.2em]">{label}</span>
                  <span className="text-xs text-dim">{t.insight.noEntry}</span>
                </li>
              );
            }

            return (
              <li key={word}>
                <button
                  type="button"
                  aria-label={t.insight.revealFor(label)}
                  disabled={insightsLeft === 0 || spendingWords.has(word)}
                  onClick={() => onReveal(word)}
                  className="flex min-h-[52px] w-full items-center justify-between rounded-xl bg-black/20 px-3 disabled:opacity-50"
                >
                  <span className="text-sm font-semibold tracking-[0.2em]">{label}</span>
                  <span className="flex items-center gap-1 text-xs text-dim">
                    {t.insight.reveal}
                    <ChevronRightIcon className="h-4 w-4" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="border-t border-hairline pt-3 text-xs text-dim">
        {t.insight.leftToday(insightsLeft)}
      </p>
    </Sheet>
  );
}
